import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../hooks/useConfirm';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Icon from '../../components/AppIcon';
import RequestFormFields from './components/RequestFormFields';
import { supplierRequestService } from '../../services/apiService';

const SupplierRequestForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [formData, setFormData] = useState({
    user_name: '',
    user_site_plant: '',
    required_vendor_name: '',
    vendor_contact: '',
    vendor_email: '',
    reason_for_new_vendor: '',
    requested_by: ''
  });
  const [draftRequests, setDraftRequests] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch user's draft requests on mount
  useEffect(() => {
    fetchDraftRequests();
  }, []);

  // Handle URL parameters for direct navigation (continue draft)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draftId = params.get('draftId');

    if (draftId) {
      loadDraftById(parseInt(draftId));
    }
  }, []);

  const loadDraftById = async (draftId) => {
    try {
      const result = await supplierRequestService.getRequestById(draftId);
      const draft = result.data;
      setCurrentDraftId(draft.id);
      setFormData({
        user_name: draft.user_name || '',
        user_site_plant: draft.user_site_plant || '',
        required_vendor_name: draft.required_vendor_name || '',
        vendor_contact: draft.vendor_contact || '',
        vendor_email: draft.vendor_email || '',
        reason_for_new_vendor: draft.reason_for_new_vendor || '',
        requested_by: draft.requested_by || ''
      });
      
      // Clear URL parameters
      navigate('/supplier-request-form', { replace: true });
    } catch (err) {
      console.error('Error loading draft:', err);
      setError('Failed to load draft request');
    }
  };

  const fetchDraftRequests = async () => {
    try {
      const result = await supplierRequestService.getSupplierRequests({ status: 'DRAFT' });
      setDraftRequests(result.data || []);
    } catch (err) {
      console.error('Error fetching draft requests:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'user_name',
      'user_site_plant',
      'required_vendor_name',
      'vendor_contact',
      'vendor_email',
      'reason_for_new_vendor',
      'requested_by'
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        errors[field] = `${fieldLabel} is required`;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.vendor_email && !emailRegex.test(formData.vendor_email)) {
      errors.vendor_email = 'Please enter a valid email address';
    }

    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setError('Please fix the errors in the form');
      return false;
    }

    setFieldErrors({});
    setError(null);
    return true;
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      if (currentDraftId) {
        result = await supplierRequestService.updateDraft(currentDraftId, formData);
      } else {
        result = await supplierRequestService.createDraft(formData);
      }
      
      const savedDraft = result.data;
      setCurrentDraftId(savedDraft.id);
      setSuccessMessage('Draft saved successfully!');
      fetchDraftRequests();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      if (currentDraftId) {
        result = await supplierRequestService.updateDraft(currentDraftId, formData);
      } else {
        result = await supplierRequestService.createDraft(formData);
      }

      const savedDraft = result.data;
      setCurrentDraftId(savedDraft.id);
      await supplierRequestService.submitRequest(savedDraft.id);
      setSuccessMessage('Request submitted successfully!');
      setTimeout(() => {
        navigate('/supplier-requests');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDraft = (draft) => {
    setFormData({
      user_name: draft.user_name,
      user_site_plant: draft.user_site_plant,
      required_vendor_name: draft.required_vendor_name,
      vendor_contact: draft.vendor_contact,
      vendor_email: draft.vendor_email,
      reason_for_new_vendor: draft.reason_for_new_vendor,
      requested_by: draft.requested_by
    });
    setCurrentDraftId(draft.id);
    setSuccessMessage('Draft loaded successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteDraft = async (draftId) => {
    const confirmed = await confirm({
      title: 'Delete Draft',
      message: 'Are you sure you want to delete this draft?',
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    try {
      await supplierRequestService.deleteRequest(draftId);

      setSuccessMessage('Draft deleted successfully!');
      fetchDraftRequests();
      
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
        setFormData({
          user_name: '',
          user_site_plant: '',
          required_vendor_name: '',
          vendor_contact: '',
          vendor_email: '',
          reason_for_new_vendor: '',
          requested_by: ''
        });
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete draft');
    }
  };

  const handleNewForm = () => {
    setCurrentDraftId(null);
    setFormData({
      user_name: '',
      user_site_plant: '',
      required_vendor_name: '',
      vendor_contact: '',
      vendor_email: '',
      reason_for_new_vendor: '',
      requested_by: ''
    });
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb 
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'Supplier Request Form', path: '/supplier-request-form' }
        ]} 
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Supplier Request Form
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Request creation of a new vendor code
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Card className="border border-emerald-200 shadow-sm bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-emerald-600" />
              <span className="text-emerald-800">{successMessage}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="border border-red-200 shadow-sm bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Draft Requests Section */}
      {draftRequests.length > 0 && (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Your Draft Requests</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleNewForm}
                iconName="Plus"
                iconPosition="left"
              >
                New Form
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {draftRequests.map(draft => (
                <Card key={draft.id} className="border border-slate-200 hover:shadow-md transition-all duration-200">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">{draft.required_vendor_name || 'Untitled Draft'}</h3>
                        <p className="text-sm text-slate-600 mb-1">
                          Created: {new Date(draft.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleLoadDraft(draft)}
                          iconName="Edit"
                          iconPosition="left"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteDraft(draft.id)}
                          iconName="Trash2"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardHeader>
          <CardTitle>Form Details</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestFormFields formData={formData} onChange={handleInputChange} errors={fieldErrors} />

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={loading}
              className="flex-1"
              iconName="Save"
              iconPosition="left"
            >
              Save as Draft
            </Button>
            <Button
              variant="default"
              onClick={handleSubmitForApproval}
              disabled={loading}
              className="flex-1"
              iconName="Send"
              iconPosition="left"
            >
              Submit for Approval
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default SupplierRequestForm;

